import { AUTH_EVENTS, postAuthEvent } from "../../../../utils/authChannel";
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import {
  useResetPasswordMutation,
  useResetSetTokenVerifyMutation,
  useLogOutUserMutation,
} from "../../../../store/auth/usersApiSlice";
import { logout, clearUserInfo } from "../../../../store/auth/authSlice";

/* Step 3's rules — the whole of them. */

/* The verify-failure precedence, transcribed from handleVerifyToken's catch: error.data.message → error.data.errors.non_field_errors[0] → fallback. */
export const verifyRejectionMessage = (error) =>
  error?.data?.message ||
  error?.data?.errors?.non_field_errors?.[0] ||
  "This password reset link has expired.";

export const verifyInvalidMessage = (res) =>
  res?.errors?.non_field_errors?.[0] ||
  "This password reset link is invalid or expired.";

/* The submit-failure precedence, transcribed from handleSubmit's catch. */
export const submitErrorMessage = (error) =>
  error?.data?.email?.[0] ||
  error?.data?.non_field_errors?.[0] ||
  "Please try again";

/* Where the success dialog sends the user, transcribed from handleNavigate:
   a `prompt` param wins outright; otherwise a new user lands on the dashboard
   login and everyone else on plain login. */
export const destinationAfterReset = ({ prompt, newUser }) => {
  if (prompt) return "/login/prompt";
  return newUser ? "/login/dashboard" : "/login";
};

export const useSetPassword = ({ route = null } = {}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { token: existingSessionToken } = useSelector((state) => state.auth);

  const [isOpen, setIsOpen] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [passwordError, setPasswordError] = useState("");

  const [newUser, setNewUser] = useState(false);
  const [token, setToken] = useState("");
  const [email, setEmail] = useState("");
  const [prompt, setPrompt] = useState("");

  const [isVerifyingToken, setIsVerifyingToken] = useState(true);
  const [isTokenInvalid, setIsTokenInvalid] = useState(false);
  const [tokenErrorMessage, setTokenErrorMessage] = useState("");

  const [resetPassword, { isLoading }] = useResetPasswordMutation();
  const [resetSetTokenVerify] = useResetSetTokenVerifyMutation();
  const [logOutUser] = useLogOutUserMutation();

  const handleVerifyToken = useCallback(
    async (userEmail, userToken) => {
      try {
        setIsVerifyingToken(true);
        setIsTokenInvalid(false);

        const res = await resetSetTokenVerify({
          token: userToken,
          email: userEmail,
          create: route === "set",
        }).unwrap();

        /* Transcribed: only an explicit `valid === false` is a failure. A body
           without the key at all is treated as valid, which is what the live
           screen does. */
        if (res?.valid === false) {
          setIsTokenInvalid(true);
          setTokenErrorMessage(verifyInvalidMessage(res));
        }
      } catch (error) {
        setIsTokenInvalid(true);
        setTokenErrorMessage(verifyRejectionMessage(error));
      } finally {
        setIsVerifyingToken(false);
      }
    },
    [resetSetTokenVerify, route]
  );

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);

    const userNew = urlParams.get("new_user");
    const userEmail = urlParams.get("email");
    const userToken = urlParams.get("token");
    const userPrompt = urlParams.get("prompt");

    if (userEmail && userToken) {
      setToken(userToken);
      setEmail(userEmail);
      setPrompt(userPrompt);
      setNewUser(userNew);
      handleVerifyToken(userEmail, userToken);
    } else {
      /* Transcribed EXACTLY, including what is missing: this branch sets the invalid flag but never clears isVerifyingToken, which starts true. */
      setIsTokenInvalid(true);
      setTokenErrorMessage("Invalid password reset link.");
    }
    /* Transcribed: the live effect runs once on mount with an empty list. */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      setPasswordError("Password did not match");
      return;
    }

    if (email === "" || token === "") {
      setPasswordError("Please try again!");
      return;
    }

    try {
      const res = await resetPassword({
        token,
        email,
        new_password: password,
        create: route === "set",
      }).unwrap();

      if (res) {
        setIsOpen(true);
      }
    } catch (error) {
      setPasswordError(submitErrorMessage(error));
    }
  };

  /* Transcribed: leaving the dialog ends any session that was already open —
     the password just changed underneath it, so the old access token must not
     survive into the next screen. */
  const handleNavigate = async () => {
    setIsOpen(false);

    if (existingSessionToken) {
      await logOutUser({ token: existingSessionToken });

    // Tell the other tabs before clearing this one. They share the cookies
    // that were just revoked, so without this they keep rendering a signed-in
    // UI until their next 401 — which on an idle page may never come.
    postAuthEvent(AUTH_EVENTS.SIGNED_OUT);

      dispatch(logout());
      dispatch(clearUserInfo());
    }

    navigate(destinationAfterReset({ prompt, newUser }));
  };

  return {
    password,
    setPassword,
    confirmPassword,
    setConfirmPassword,
    showPassword,
    toggleShowPassword: () => setShowPassword((v) => !v),
    passwordError,
    isLoading,
    isOpen,
    newUser,
    isVerifyingToken,
    isTokenInvalid,
    tokenErrorMessage,
    handleSubmit,
    handleNavigate,
  };
};

export default useSetPassword;
